import { ColumnType, TableProps } from "@ad/anq-ui/lib/_components/_table";
import TooltipTitle, { TooltipType } from "./tooltip_title";
import classNames from "classnames";
import moment from "moment";

export const BACKEND_TIME_FORMAT = "YYYY-MM-DD";
export const FRONTEND_TIME_FORMAT = "YYYY/MM/DD";

export interface DmpColumnType<T = object> extends ColumnType<T> {
  /** 标题带tooltip */
  tooltip?: React.ReactNode;
  tooltipType?: TooltipType;
  /** 这一列灰色背景包括表头在内 */
  gray?: boolean;
  /** 这一列白色色背景包括表头在内 */
  white?: boolean;
  /** 标题的className */
  titleClassName?: string;
  /** 该列是时间, 默认YYYY/MM/DD; 此时render无效 */
  isTime?: boolean;
  /** 该列是时间, 默认YYYY/MM/DD ~ YYYY/MM/DD; 此时render无效 */
  isTimeRange?: { keys: [keyof T, keyof T] };
}

export interface DmpColumnGroupType<T>
  extends Omit<DmpColumnType<T>, "dataIndex"> {
  children: Props<T>["columns"];
}

export interface Props<T = any> extends Omit<TableProps<T>, "columns"> {
  /** 展示在table上面的东西, table无缝衔接 */
  tableTop?: React.ReactNode;
  tableTopStick?: number;
  columns: (DmpColumnGroupType<T> | DmpColumnType<T>)[];
  fixedHeader?: boolean | number;
  /** 合计行 */
  sumRow?: {
    /** 合计行的"合计数据"文本展示列的dataIndex/key */
    textColumn?: string;
    /** 默认: 合计数据（n 条） */
    renderText?: (total: number) => React.ReactNode;
    /** 合计行的空白列的dataIndex/key或者动态判断是否是空白 */
    emptyColumns?:
      | ((column: DmpColumnGroupType<T> | DmpColumnType<T>) => boolean)
      | string[];
    /** 合计行的数据列的dataIndex/key或者动态判断是否是空白; 默认除了空白和文本列都是数据列 */
    dataColumns?:
      | ((column: DmpColumnGroupType<T> | DmpColumnType<T>) => boolean)
      | string[];
    /**
     * 合计行数据计算规则, 根据默认的求和数据计算后得出实际的合计行数据
     * @example { cpm: (sumRowData.spd / sumRowData.imprCnt) * 1000 } 千次曝光花费=求和的花费/求和的曝光次数*1000
     * @example { mallPayCnt: undefined } 店铺新客数求和无意义展示为空
     * @param sumRowData 默认的合计行的数据, 默认就是row求和
     * @returns 计算后的数据
     */
    dataCalculator?: (sumRowData: Partial<T>) => Partial<T>;
  };
}

export const getAllColumns = (
  columns: Props["columns"]
): DmpColumnType<any>[] => {
  return columns
    .map((item) => {
      if ("children" in item) {
        return getAllColumns(item.children);
      }

      return item;
    })
    .flat();
};

/**
 * 增加一些自定义的功能:
 *   * tooltip
 *   * 整列灰色/白色背景
 *   * 时间格式化
 *   * 合计行
 *   * ...
 */
export const fixColumns = (
  columns: Props["columns"],
  sumRow?: Props["sumRow"],
  sumRowData?: any,
  total?: number
) => {
  return columns.map((column) => {
    const {
      title,
      gray,
      tooltip,
      white,
      titleClassName,
      isTime,
      isTimeRange,
      tooltipType,
      ...rest
    } = column;

    if ("children" in column) {
      return fixColumns(column.children, sumRow, sumRow, total);
    }

    const columnWithType = column as DmpColumnType;

    const result = {
      ...rest,
      title: (
        <div className={titleClassName}>
          {tooltip && typeof title !== "function" ? (
            <TooltipTitle title={title} tooltip={tooltip} type={tooltipType} />
          ) : (
            title
          )}
        </div>
      ),
      className: classNames(
        gray ? "bg-[#FAFAFC] gray-cell" : "",
        white ? "white-cell" : ""
      ),
    };

    if (isTime) {
      result.render = (text) => moment(text).format(FRONTEND_TIME_FORMAT);
    }

    if (isTimeRange) {
      const [startKey, endKey] = isTimeRange.keys;

      result.render = (_, record) => {
        const startTime = record[startKey];
        const endTime = record[endKey];

        return `${moment(startTime).format(FRONTEND_TIME_FORMAT)} ~ ${moment(
          endTime
        ).format(FRONTEND_TIME_FORMAT)}`;
      };
    }

    if (sumRow) {
      const preRender = result.render;

      const columnKey = columnWithType.key || columnWithType.dataIndex;
      const isTextColumn = sumRow?.textColumn === columnKey;
      const isEmptyColumn =
        (typeof sumRow?.emptyColumns === "function" &&
          sumRow?.emptyColumns(columnWithType)) ||
        (Array.isArray(sumRow?.emptyColumns) &&
          sumRow?.emptyColumns.includes(columnKey as string));

      result.render = (text, record, index) => {
        if (index) {
          return preRender?.(text, record, index);
        }

        if (isTextColumn) {
          return (
            <span className="font-medium">
              {sumRow?.renderText
                ? sumRow.renderText(total)
                : `合计数据（${total} 条）`}
            </span>
          );
        }

        if (isEmptyColumn) {
          return "";
        }

        const data = sumRowData[rest.key] || text;

        return (
          <span className="font-medium">
            {preRender?.(data, record, index) || data}
          </span>
        );
      };
    }

    return result;
  });
};

export const renderSumRow = (wrapperDom: HTMLDivElement) => {
  //  最后是否成功插入了 合计行的dom
  let success = false;

  if (wrapperDom) {
    // 当前Table的dom
    const bodyDom = wrapperDom.querySelector(".anq-table-tbody");

    if (bodyDom) {
      // 当前Table如果没数据的dom
      const emptyDom = wrapperDom.querySelector(
        ".DmpTable__copy .anq-table-empty"
      );

      if (emptyDom) {
        const existedCopyRow = bodyDom.querySelector(".DmpTable__copy__row");

        if (existedCopyRow) {
          existedCopyRow.remove();
        }
      } else {
        const copyDom = wrapperDom
          .querySelector(".DmpTable__copy .anq-table-row.anq-table-row-level-0")
          ?.cloneNode(true) as HTMLDivElement;

        if (copyDom) {
          copyDom.classList.add("DmpTable__copy__row");

          const existedCopyRow = bodyDom.querySelector(".DmpTable__copy__row");

          if (existedCopyRow) {
            existedCopyRow.remove();
          }

          bodyDom.insertBefore(copyDom, bodyDom.children[1]);
          success = true;
        }
      }
    }
  }

  return success;
};
